import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  ArrowLeftRight,
  Shield,
  AlertTriangle,
  Check,
  Loader2,
  TrendingUp,
  BadgeCheck,
  Calendar,
} from "lucide-react";
import { cn } from "@/lib/utils";

async function callBillingModelApi(accessToken: string, action: string, params?: any) {
  const res = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/billing-model-api`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ action, ...params }),
    }
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Anfrage fehlgeschlagen");
  return data;
}

export default function BillingModelSwitch() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [confirmSwitch, setConfirmSwitch] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["billing-model"],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");
      return callBillingModelApi(session.access_token, "get_billing_model");
    },
    refetchInterval: 30_000,
  });

  const switchMutation = useMutation({
    mutationFn: async (targetModel: string) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");
      return callBillingModelApi(session.access_token, "switch_billing_model", { target_model: targetModel });
    },
    onSuccess: (result) => {
      toast.success(result.message);
      setConfirmSwitch(null);
      queryClient.invalidateQueries({ queryKey: ["billing-model"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const cancelMutation = useMutation({
    mutationFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");
      return callBillingModelApi(session.access_token, "cancel_switch");
    },
    onSuccess: () => {
      toast.success("Wechsel abgebrochen");
      queryClient.invalidateQueries({ queryKey: ["billing-model"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  if (isLoading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-accent" />
      </div>
    );
  }

  const currentModel = data?.billing_model || "ppc";
  const pendingSwitch = data?.pending_switch;
  const disputeStats = data?.dispute_stats;

  const models = [
    {
      key: "ppc",
      name: "Pay-per-Contact",
      desc: "Zahlen Sie nur für qualifizierte, verifizierte Kontakte. Kein Fixkostenrisiko.",
      icon: TrendingUp,
      benefits: [
        "Keine monatlichen Fixkosten",
        "Nur qualifizierte Leads (Score > 60)",
        "Ideal für kleine Betriebe",
        "Volle Kostenkontrolle",
      ],
    },
    {
      key: "subscription",
      name: "Abo-Modell",
      desc: "Planbare Kosten mit inkludierten Kontakten und reduziertem Kontaktpreis.",
      icon: BadgeCheck,
      benefits: [
        "Kontakte im Abo inklusive",
        "20-40% reduzierte Kontaktpreise",
        "Premium-Sichtbarkeit",
        "Ranking-Boost bei Matching",
      ],
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-subheadline flex items-center gap-2">
          <ArrowLeftRight className="w-5 h-5" />
          Abrechnungsmodell
        </h3>
      </div>

      {/* Pending switch notice */}
      {pendingSwitch && (
        <div className="p-4 bg-accent/5 border border-accent/20 flex items-start gap-3">
          <Calendar className="w-5 h-5 text-accent shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium">
              Wechsel zu {pendingSwitch.to_model === "ppc" ? "Pay-per-Contact" : "Abo-Modell"} geplant
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Wird aktiv ab {new Date(pendingSwitch.effective_at).toLocaleDateString("de-DE")}
            </p>
          </div>
          <Button
            variant="quiet"
            size="sm"
            onClick={() => cancelMutation.mutate()}
            disabled={cancelMutation.isPending}
          >
            Abbrechen
          </Button>
        </div>
      )}

      {/* Model cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {models.map((model) => {
          const isCurrent = currentModel === model.key;
          const isPending = pendingSwitch?.to_model === model.key;
          return (
            <div
              key={model.key}
              className={cn(
                "p-6 border bg-card flex flex-col transition-all",
                isCurrent ? "border-accent" : "border-border hover:border-accent/30"
              )}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-2">
                  <model.icon className="w-5 h-5 text-accent" />
                  <h4 className="text-body font-medium">{model.name}</h4>
                </div>
                {isCurrent && (
                  <span className="text-xs px-2 py-0.5 bg-accent/10 text-accent border border-accent/30">
                    Aktiv
                  </span>
                )}
                {isPending && (
                  <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary border border-primary/30">
                    Geplant
                  </span>
                )}
              </div>
              <p className="text-caption text-sm mb-4">{model.desc}</p>
              <ul className="space-y-2 flex-1">
                {model.benefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                    <Check className="w-3 h-3 text-accent shrink-0 mt-0.5" />
                    {b}
                  </li>
                ))}
              </ul>
              {!isCurrent && !isPending && (
                <div className="mt-4">
                  {confirmSwitch === model.key ? (
                    <div className="flex gap-2">
                      <Button
                        variant="commit"
                        size="sm"
                        className="flex-1"
                        onClick={() => switchMutation.mutate(model.key)}
                        disabled={switchMutation.isPending}
                      >
                        {switchMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Bestätigen"}
                      </Button>
                      <Button variant="quiet" size="sm" onClick={() => setConfirmSwitch(null)}>
                        Abbrechen
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="quiet"
                      size="sm"
                      className="w-full"
                      onClick={() => setConfirmSwitch(model.key)}
                    >
                      Wechseln (ab nächstem Monat)
                    </Button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Dispute / Fairness stats */}
      {disputeStats && (
        <div className="p-5 border border-border bg-card space-y-4">
          <h4 className="text-body font-medium flex items-center gap-2">
            <Shield className="w-4 h-4 text-accent" />
            Fairness-Garantie
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <p className="text-2xl font-semibold">{disputeStats.total_accepted}</p>
              <p className="text-xs text-muted-foreground">Kontakte gesamt</p>
            </div>
            <div>
              <p className="text-2xl font-semibold">{disputeStats.filed}</p>
              <p className="text-xs text-muted-foreground">Reklamationen</p>
            </div>
            <div>
              <p className={cn("text-2xl font-semibold", disputeStats.rate_pct > 5 ? "text-destructive" : "text-accent")}>
                {disputeStats.rate_pct}%
              </p>
              <p className="text-xs text-muted-foreground">Reklamationsrate</p>
            </div>
            <div>
              <p className="text-2xl font-semibold">
                {disputeStats.auto_credit_available ? (
                  <Check className="w-6 h-6 text-accent mx-auto" />
                ) : (
                  <AlertTriangle className="w-6 h-6 text-destructive mx-auto" />
                )}
              </p>
              <p className="text-xs text-muted-foreground">Auto-Gutschrift</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Bis zu 5% Ihrer monatlichen Kontakte können automatisch reklamiert werden. Bei CRAFTFINDUS zahlen Sie nur für echte, qualifizierte Kontakte.
          </p>
        </div>
      )}
    </div>
  );
}
